import * as React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

import { cn } from "../../lib/utils";

const PAGE_SIZES = [10, 25, 50, 100] as const;
const PAGE_SIZE_ALL = -1;

export interface DataPaginationState {
  page: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
}

export interface DataPaginationProps {
  /** Current pagination state */
  state: DataPaginationState;
  /** Callback when page changes */
  onPageChange: (page: number) => void;
  /** Callback when page size changes */
  onPageSizeChange: (size: number) => void;
  /** Custom page size options (defaults to shared PAGE_SIZES) */
  pageSizes?: readonly number[];
  /**
   * Whether to show the page-size select. Defaults to true when more than one
   * size is available (including "All"); false for fixed-page lists.
   */
  showPageSizeSelect?: boolean;
  /** Whether to show "All" option */
  showAllOption?: boolean;
  /** Locale for item counts */
  locale?: string;
  /** Custom class name */
  className?: string;
  /** Label translations */
  labels?: {
    showing?: string;
    showingAll?: string;
    of?: string;
    perPage?: string;
    all?: string;
  };
}

/**
 * DataPagination component for table pagination
 *
 * A higher-level pagination component designed for data tables
 * with page size selection and Swedish labels by default.
 *
 * @example
 * <DataPagination
 *   state={{ page: 1, pageSize: 25, totalItems: 100, totalPages: 4 }}
 *   onPageChange={(page) => setPage(page)}
 *   onPageSizeChange={(size) => setPageSize(size)}
 * />
 */
const DataPagination = React.forwardRef<HTMLDivElement, DataPaginationProps>(
  (
    {
      state,
      onPageChange,
      onPageSizeChange,
      pageSizes = PAGE_SIZES,
      showPageSizeSelect,
      showAllOption = true,
      locale = "sv-SE",
      className,
      labels = {},
    },
    ref,
  ) => {
    const {
      showing = "Visar",
      showingAll = "Visar alla",
      of = "av",
      perPage = "per sida",
      all = "Alla",
    } = labels;

    const isShowingAll = state.pageSize === PAGE_SIZE_ALL;
    const startItem = isShowingAll ? 1 : (state.page - 1) * state.pageSize + 1;
    const endItem = isShowingAll
      ? state.totalItems
      : Math.min(state.page * state.pageSize, state.totalItems);
    const pageSizeId = React.useId();
    const numberFormatter = React.useMemo(
      () => new Intl.NumberFormat(locale),
      [locale],
    );

    const selectablePageSizes = React.useMemo(() => {
      if (pageSizes.includes(state.pageSize) || isShowingAll) {
        return pageSizes;
      }
      // Keep the controlled value visible even if callers pass a custom size.
      return [...pageSizes, state.pageSize].sort((a, b) => a - b);
    }, [isShowingAll, pageSizes, state.pageSize]);

    const shouldShowPageSizeSelect =
      showPageSizeSelect ?? (selectablePageSizes.length > 1 || showAllOption);

    /**
     * Generates visible page numbers with smart ellipsis
     */
    const getVisiblePages = (): number[] => {
      const maxVisible = 5;
      if (state.totalPages <= maxVisible) {
        return Array.from({ length: state.totalPages }, (_, i) => i + 1);
      }

      if (state.page <= 3) {
        return Array.from({ length: maxVisible }, (_, i) => i + 1);
      }

      if (state.page >= state.totalPages - 2) {
        return Array.from(
          { length: maxVisible },
          (_, i) => state.totalPages - maxVisible + 1 + i,
        );
      }

      return Array.from({ length: maxVisible }, (_, i) => state.page - 2 + i);
    };

    return (
      <div
        ref={ref}
        className={cn(
          "flex flex-col md:flex-row justify-between items-center gap-4 px-4 py-3 border-t border-border",
          className,
        )}
      >
        <div className="flex items-center gap-4 text-sm text-muted-foreground">
          <span>
            {isShowingAll
              ? `${showingAll} ${numberFormatter.format(state.totalItems)}`
              : `${showing} ${startItem}-${endItem} ${of} ${numberFormatter.format(state.totalItems)}`}
          </span>
          {shouldShowPageSizeSelect ? (
            <>
              <label htmlFor={pageSizeId} className="sr-only">
                {perPage}
              </label>
              <select
                id={pageSizeId}
                name="page-size"
                aria-label={perPage}
                value={state.pageSize}
                onChange={(e) => onPageSizeChange(parseInt(e.target.value, 10))}
                className="rounded border border-border bg-background px-2 py-1 text-sm text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              >
                {selectablePageSizes.map((size) => (
                  <option key={size} value={size}>
                    {size} {perPage}
                  </option>
                ))}
                {showAllOption && <option value={PAGE_SIZE_ALL}>{all}</option>}
              </select>
            </>
          ) : null}
        </div>

        {!isShowingAll && state.totalPages > 1 && (
          <div className="flex items-center gap-2">
            <button
              onClick={() => onPageChange(state.page - 1)}
              disabled={state.page <= 1}
              className="p-2 text-muted-foreground hover:text-foreground disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              aria-label="Föregående sida"
            >
              <ChevronLeft aria-hidden="true" size={18} />
            </button>

            {getVisiblePages().map((pageNum) => (
              <button
                key={pageNum}
                onClick={() => onPageChange(pageNum)}
                className={cn(
                  "w-8 h-8 rounded text-sm font-medium transition-colors",
                  pageNum === state.page
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:bg-muted",
                )}
                aria-current={pageNum === state.page ? "page" : undefined}
              >
                {pageNum}
              </button>
            ))}

            <button
              onClick={() => onPageChange(state.page + 1)}
              disabled={state.page >= state.totalPages}
              className="p-2 text-muted-foreground hover:text-foreground disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              aria-label="Nästa sida"
            >
              <ChevronRight aria-hidden="true" size={18} />
            </button>
          </div>
        )}
      </div>
    );
  },
);
DataPagination.displayName = "DataPagination";

export { DataPagination, PAGE_SIZE_ALL, PAGE_SIZES };
